import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BadgeCheck, KeyRound } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { API } from "../../constant/api";
import { useUser } from "../../context/user-context";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import {
  formatPhoneNumberPreview,
  parseToE164Phone,
  sanitizePhoneInput,
} from "../../utils/phoneE164";
import OtpConfirmDialog from "./components/OtpConfirmDialog";
import ReadOnlyField from "./components/ReadOnlyField";
import SettingsSection from "./components/SettingsSection";

const Profile = () => {
  const navigate = useNavigate();
  const axiosInstance = useAuthAxios();
  const { merchantDetails, refetch2 } = useUser();

  const user = merchantDetails?.user;
  const profile = user?.profile;

  /**
   * email and phone come back as objects — { address, isVerified } and
   * { number, isVerified } — not strings. Accept either shape in case the
   * payload changes, the same way AppNavbar does.
   */
  const rawEmail = user?.email;
  const email: string | undefined =
    typeof rawEmail === "string" ? rawEmail : rawEmail?.address;

  const rawPhone = user?.phone;
  const savedPhone: string =
    (typeof rawPhone === "string" ? rawPhone : rawPhone?.number) ?? "";

  const [phone, setPhone] = useState(savedPhone);
  const [otpOpen, setOtpOpen] = useState(false);

  // Seed the field once the user request resolves.
  useEffect(() => {
    setPhone(savedPhone);
  }, [savedPhone]);

  // The API stores E.164, so compare in that form — "08012345678" and
  // "+2348012345678" are the same number typed two ways.
  const e164 = parseToE164Phone(phone);
  const changed = (e164 ?? phone) !== savedPhone;
  const phoneValid = !!e164;

  const submitPhone = async (otp: string) => {
    try {
      const { data } = await axiosInstance.patch(API.phone, {
        phoneNumber: e164,
        otp,
      });
      toast.success(data?.message || "Phone number updated");
      refetch2();
      return data;
    } catch (e: any) {
      toast.error(errorMessage(e?.response?.data?.message || e?.message));
      throw e;
    }
  };

  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Personal details"
        description="Verified against your identity documents. Contact support to change these."
      >
        <div className="grid grid-cols-1 gap-4 mds:grid-cols-2">
          <ReadOnlyField label="First name" value={profile?.firstName} />
          <ReadOnlyField label="Last name" value={profile?.lastName} />
          <div className="mds:col-span-2">
            <ReadOnlyField
              label="Email address"
              value={email}
            />
          </div>
        </div>

        {fullName && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-accent px-4 py-3">
            <BadgeCheck className="size-4 shrink-0 text-primary" />
            <p className="text-[13px] font-medium text-primary">
              Verified as {fullName}
            </p>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Phone number"
        description="Used for account notifications. Changing it requires a one-time code."
        action={
          <Button
            onClick={() => setOtpOpen(true)}
            disabled={!changed || !phoneValid}
            className="h-10 rounded-xl px-5 text-[14px] font-semibold"
          >
            Save changes
          </Button>
        }
      >
        <div className="max-w-[420px] space-y-1.5">
          <Label htmlFor="profile-phone">Phone number</Label>
          <Input
            id="profile-phone"
            type="tel"
            inputMode="tel"
            placeholder="08012345678 or +2348012345678"
            value={phone}
            onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))}
            aria-invalid={phone.length > 0 && !phoneValid}
          />
          {phone.length > 0 && !phoneValid ? (
            <p className="text-[12px] font-medium text-destructive">
              Enter a valid phone number.
            </p>
          ) : (
            phoneValid &&
            changed && (
              <p className="text-[12px] font-medium text-muted-foreground">
                Will be saved as {formatPhoneNumberPreview(phone)}
              </p>
            )
          )}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Login & password"
        description="Manage the password you use to sign in."
      >
        <button
          type="button"
          onClick={() => navigate("/settings/security")}
          className="flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-muted"
        >
          <KeyRound className="size-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-semibold text-foreground">
              Change password
            </span>
            <span className="block text-[12px] font-medium text-muted-foreground">
              Set a new password from the Security tab
            </span>
          </span>
        </button>
      </SettingsSection>

      <OtpConfirmDialog
        open={otpOpen}
        onOpenChange={setOtpOpen}
        purpose="edit-profile"
        title="Confirm phone change"
        description={`Enter the 6-digit code we sent to ${
          email ?? "your email"
        }.`}
        confirmLabel="Update phone number"
        onConfirm={submitPhone}
      />
    </div>
  );
};

export default Profile;
